import { useEffect, useState } from 'react';
import MediaQuery from 'react-responsive';
import ApiDatabase from 'server';
import { Plus, SearchLoop, Filter } from '../../assets/icons';
import Button from '../../components/button';
import PageForm from '../../components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import StyledInput from '../../components/styledInput/StyledInput';
import Pagination from '../../components/pagination/pagination';
import useSlideOver from '../../components/SlideOver/useSlideOver';
import Table from '../../components/table/table';
import CompanyMobileCard from '../../components/card/MobileListCard/CompanyMobileCard';
import UseContext from '../../hooks/useContext';
import BottomSheet from '../../components/bottomSheet/BottomSheet';
import EmptyState from '../../components/EmptyState/EmptyState';

const SEARCH_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'id_company', label: 'ID Entreprise' },
  { value: 'name_company', label: 'Nom entreprise' },
  { value: 'group_company', label: 'Groupe' },
  { value: 'employee_name', label: 'Nom Employé' },
  { value: 'siret', label: 'Siret' },
];

const TITLES = ['ENSEIGNE', 'GROUPE', 'REJOINT LE'];

const SearchTypeSelect = ({ value, onChange, className = '' }) => (
  <select
    className={`w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-md text-sm focus:border-yellow-500 focus:outline-none ${className}`}
    onChange={(e) => onChange(e.target.value)}
    value={value}
  >
    {SEARCH_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const MobileCardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 animate-pulse">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="flex justify-between items-center">
          <div className="h-4 bg-gray-100 rounded w-24" />
          <div className="h-4 bg-gray-100 rounded w-28" />
        </div>
      </div>
    ))}
  </div>
);

const TableSkeleton = () => (
  <table className="animate-pulse w-full">
    <tbody>
      {[1, 2, 3, 4, 5].map((i) => (
        <tr key={i}>
          {TITLES.map((_, idx) => (
            <td
              key={idx}
              className={`py-7 ${idx === 0 ? 'pl-4' : ''} ${idx === TITLES.length - 1 ? 'pr-3' : 'px-2'}`}
            >
              <div className="h-2 bg-gray-200 rounded" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const Company = () => {
  const [isWaiting, setIsWaiting] = useState(true);
  const [companies, setCompanies] = useState([]);
  const { toggleSlideOver } = useSlideOver();
  const { setObjID } = UseContext();
  const [search, setSearch] = useState('');
  const [optionSearch, setOptionSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [pennylaneCategoryList, setPennylaneCategoryList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(0);
  const limitElement = 20;
  const [countTotal, setCountTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempSearch, setTempSearch] = useState('');
  const [tempOptionSearch, setTempOptionSearch] = useState('');
  const [tempFilterCategory, setTempFilterCategory] = useState('');

  const token = localStorage.getItem('xsrfToken');

  const openFilterSheet = () => {
    setTempSearch(search);
    setTempOptionSearch(optionSearch);
    setTempFilterCategory(filterCategory);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSearch(tempSearch);
    setOptionSearch(tempOptionSearch);
    setFilterCategory(tempFilterCategory);
    setIsFilterOpen(false);
  };

  const manageCompaniesSearch = () => {
    setIsWaiting(true);
    ApiDatabase.getCompanies(
      { token, search, optionSearch, filterCategory, page: currentPage, limit: limitElement },
      (data) => {
        setCompanies(data.companies);
        setTotalPages(data.countPages);
        setCountPerPage(data.countPerPage);
        setCountTotal(data.count);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); }
    );
  };

  useEffect(() => {
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => setPennylaneCategoryList(data?.items || []),
      (err) => { if (err.name !== 'CanceledError') console.error(err); }
    );
  }, []);

  useEffect(() => {
    manageCompaniesSearch();
  }, [currentPage, limitElement]);

  useEffect(() => {
    setCurrentPage(1);
    const timer = setTimeout(manageCompaniesSearch, 400);
    return () => clearTimeout(timer);
  }, [search, optionSearch, filterCategory]);

  const openCompanyInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_company');
  };

  return (
    <PageForm>
      <SubNavbar>
          <div className="flex justify-between items-center w-full gap-2 md:gap-3 min-w-0">
            <MediaQuery minWidth={768}>
              <div className="flex gap-2 items-center flex-1 min-w-0">
                <div className="flex-1 min-w-[120px] max-w-[400px]">
                  <StyledInput
                    type="text"
                    placeholder="Rechercher..."
                    imgFront={<SearchLoop wh={16} color="#6B7280" />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex-shrink-0 w-[140px] md:max-w-[200px]">
                  <SearchTypeSelect value={optionSearch} onChange={setOptionSearch} />
                </div>
                {pennylaneCategoryList.length > 0 && (
                  <div className="flex-shrink-0 w-[160px]">
                    <StyledInput
                      type="select"
                      value={filterCategory}
                      list={[
                        { value: '', label: 'Tous les secteurs' },
                        ...pennylaneCategoryList.map((cat) => ({ value: String(cat.id), label: cat.label })),
                      ]}
                      onChange={(e) => setFilterCategory(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </MediaQuery>

            <MediaQuery maxWidth={767}>
              <Button color="SECONDARY" size="SM" onClick={openFilterSheet}>
                <Filter wh={16} color="#374151" />
                <span>Filtrer</span>
              </Button>
            </MediaQuery>

            <div className="flex items-center gap-3 flex-shrink-0">
              <Button size="SM" color="PRIMARY" className="h-10" onClick={() => toggleSlideOver('add_company')}>
                <Plus wh={18} color="#FFFFFF" />
                <span>Ajouter</span>
              </Button>
            </div>
          </div>
        </SubNavbar>

        <div className="flex flex-col w-full h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto md:overflow-y-hidden">
            {isWaiting ? (
              <>
                <MediaQuery minWidth={768}><TableSkeleton /></MediaQuery>
                <MediaQuery maxWidth={767}><MobileCardSkeleton /></MediaQuery>
              </>
            ) : companies.length > 0 ? (
              <>
                <MediaQuery minWidth={768}>
                  <Table champsBody={companies} champsHeader={TITLES} />
                </MediaQuery>
                <MediaQuery maxWidth={767}>
                  <div className="flex flex-col gap-3 p-4">
                    {companies.map((item) => (
                      <CompanyMobileCard key={item._id} item={item} onOpen={openCompanyInfo} />
                    ))}
                  </div>
                </MediaQuery>
              </>
            ) : (
              <EmptyState type="company" />
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
            <Pagination
              currentPage={currentPage}
              countPerPage={countPerPage}
              countTotal={countTotal}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}

        <BottomSheet isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filtrer">
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
              <StyledInput
                type="text"
                placeholder="Rechercher..."
                imgFront={<SearchLoop wh={16} color="#6B7280" />}
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher par</label>
              <SearchTypeSelect value={tempOptionSearch} onChange={setTempOptionSearch} className="rounded-lg" />
            </div>
            {pennylaneCategoryList.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secteur</label>
                <StyledInput
                  type="select"
                  value={tempFilterCategory}
                  list={[
                    { value: '', label: 'Tous les secteurs' },
                    ...pennylaneCategoryList.map((cat) => ({ value: String(cat.id), label: cat.label })),
                  ]}
                  onChange={(e) => setTempFilterCategory(e.target.value)}
                />
              </div>
            )}
            <Button color="PRIMARY" size="FULL" onClick={applyFilters} className="w-full mt-2">
              Appliquer
            </Button>
          </div>
        </BottomSheet>
    </PageForm>
  );
};

export default Company;
